import {useContext, useEffect, useMemo} from 'react'
import {createProductViewModel} from '../../../helpers/createProductViewModel'
import {BuilderContext} from '@builder.io/sdk-react'
import {useInfiniteQuery} from '@tanstack/react-query'
import {usePaginationUrlParams} from 'hec/hooks/usePaginationUrlParams'
import {getCioCategories, getCioSearch} from 'hec/helpers/constructorHelpers'
import {useFetchBatchedSfccProducts} from 'hec/hooks/product/useFetchBatchedProductData'
import {useCioFacetSelector, SET_SORT_OPTIONS, SET_CIO_FACETS} from '@/context/cio-facet'
import {extractValueFromSelectedFacets} from '@/utils/facetHelpers'
import {isProduction} from '@/utils/common'
import {TEN_MINUTES_IN_MS} from '@/utils/constants'
import {useAppOrigin} from '~/app/hooks/use-app-origin'

const plpQueryFn = async ({
    appOrigin,
    pageParam = 1,
    productCategoryId = null,
    searchQuery = null,
    perPageLimit,
    selectedSortOption,
    filters
}) => {
    const response = searchQuery
        ? await getCioSearch(appOrigin, searchQuery, {
              resultsPerPage: perPageLimit,
              page: pageParam
          })
        : await getCioCategories(appOrigin, productCategoryId, {
              resultsPerPage: perPageLimit,
              page: pageParam,
              filters,
              sortBy: selectedSortOption?.sort_by || '',
              sortOrder: selectedSortOption?.sort_order || ''
          })

    const totalResults = response.response.total_num_results
    const nextPage =
        perPageLimit * (pageParam - 1) + response.response.results.length >= totalResults
            ? undefined
            : pageParam + 1

    return {
        response,
        nextPage
    }
}

export const usePlpPagination = ({perPageLimit, queryParams}) => {
    const selectedSortOption = useCioFacetSelector((context) => context?.state.selectedSortOption)
    const selectedFacetFilters = useCioFacetSelector((context) => context?.state.selectedCioFacets)
    const dispatchFacetFilters = useCioFacetSelector((context) => context?.dispatch)
    const builderContext = useContext(BuilderContext)
    const isPlpPage = builderContext?.content?.data?.isPlpPage
    const isSearchPage = !!queryParams?.q
    const searchQuery = isSearchPage && queryParams?.q
    const productCategoryId = isPlpPage
        ? builderContext?.content?.data?.productCategory?.options?.category
        : null
    const strippedFacetFilters = useMemo(() => {
        return extractValueFromSelectedFacets(selectedFacetFilters)
    }, [selectedFacetFilters])
    const {showAll, showNextEntries, getAllEntries, displayedPage} = usePaginationUrlParams()

    const appOrigin = useAppOrigin()

    const isInitialized = useCioFacetSelector((context) => context?.state.isInitialized)

    const {data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage} =
        useInfiniteQuery({
            queryKey: [
                'plp',
                'constructor-products',
                isSearchPage ? searchQuery : productCategoryId,
                strippedFacetFilters,
                selectedSortOption
            ],
            queryFn: (args) =>
                plpQueryFn({
                    appOrigin,
                    productCategoryId,
                    perPageLimit,
                    filters: strippedFacetFilters,
                    selectedSortOption,
                    ...args
                }),
            getNextPageParam: (lastPage) => {
                return lastPage.nextPage || undefined
            },
            enabled: isInitialized && (!!productCategoryId || isSearchPage),
            staleTime: isProduction ? TEN_MINUTES_IN_MS : 0
        })

    const currentPages = useMemo(() => {
        return data?.pages.flat() || []
    }, [data])

    const productIds = useMemo(() => {
        return currentPages.flatMap((item) =>
            item?.response?.response?.results.map((product) => product.data.id)
        )
    }, [currentPages])

    const {sfccProducts, isSfccProductsLoading} = useFetchBatchedSfccProducts(productIds)

    const sortOptions = useMemo(
        () => currentPages[0]?.response?.response?.sort_options,
        [currentPages]
    )

    useEffect(() => {
        if (sortOptions) {
            dispatchFacetFilters({type: SET_SORT_OPTIONS, payload: sortOptions})
        }
    }, [dispatchFacetFilters, sortOptions])

    useEffect(() => {
        if (showAll && hasNextPage && !isFetchingNextPage) {
            fetchNextPage()
        }
        if (displayedPage > currentPages.length && hasNextPage && !isFetchingNextPage) {
            fetchNextPage()
        }
    }, [
        hasNextPage,
        fetchNextPage,
        showAll,
        isFetchingNextPage,
        currentPages.length,
        displayedPage,
        perPageLimit
    ])

    const memoizedFacetsTotalResults = useMemo(() => {
        const response = data?.pages?.reduce((page) => page)?.response?.response
        return {
            facets: response?.facets,
            total_num_results: response?.total_num_results
        }
    }, [data])

    useEffect(() => {
        if (memoizedFacetsTotalResults?.facets?.length > 0) {
            dispatchFacetFilters({type: SET_CIO_FACETS, payload: memoizedFacetsTotalResults})
        }
    }, [memoizedFacetsTotalResults, dispatchFacetFilters])

    const fetchedProductData = useMemo(() => {
        if (isSfccProductsLoading) return []
        return currentPages.flatMap((item) => {
            return item?.response?.response?.results.map((product) => {
                const id = product?.data?.id
                const sfccProduct = sfccProducts?.find((p) => p.id === id) || {}
                return createProductViewModel(
                    [
                        {sourceType: 'constructor', ...product},
                        {sourceType: 'scapi', ...sfccProduct, subType: 'multi'}
                    ],
                    'constructor'
                )
            })
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPages, isSfccProductsLoading])

    const totalItemsCount = useMemo(() => {
        if (!currentPages.length) return 0
        return currentPages[0].response?.response?.total_num_results || 0
    }, [currentPages])

    return {
        showNextEntries,
        showAll,
        getAllEntries,
        fetchedProductData,
        totalItemsCount,
        allEntriesDisplayed: !hasNextPage,
        isLoading: isLoading || isSfccProductsLoading,
        isFetchingNextPage,
        error: error,
        selectedFacetFilters,
        selectedSortOption,
        productCategoryId
    }
}
